/**
 * The host's private reply to one answer: a `comment` game action, not a judgment,
 * reaching the tray through `RendererSlots`. A hook because the button and composer
 * render in different places and share which row is composing.
 */

import { useEffect, useRef, useState } from 'react';
import type { RendererSlots } from '@gsf/kit';
import type { AnswerRow } from './view';

export interface CommentSlots extends RendererSlots {
  /** Which row's composer is open, so a caller can close it on a phase change. */
  readonly commenting: string | null;
}

export function useCommentSlots(
  answers: readonly AnswerRow[],
  onComment: (participantId: string, comment: string) => void,
): CommentSlots {
  const [commenting, setCommenting] = useState<string | null>(null);
  const byId = new Map(answers.map((answer) => [answer.participantId as string, answer]));

  return {
    commenting,

    itemAction: (itemId) => {
      const answer = byId.get(itemId);
      if (!answer) return null;
      return (
        <button
          className={`btn btn-sm${answer.comment ? ' has-comment' : ''}`}
          type="button"
          title={`Reply privately to ${answer.name}`}
          onClick={() => setCommenting((open) => (open === itemId ? null : itemId))}
        >
          Reply
        </button>
      );
    },

    itemPanel: (itemId) => {
      const answer = byId.get(itemId);
      if (!answer) return null;
      if (commenting === itemId) {
        return (
          <CommentForm
            answer={answer}
            onSend={(comment) => {
              onComment(itemId, comment);
              setCommenting(null);
            }}
            onCancel={() => setCommenting(null)}
          />
        );
      }
      // The reply as it currently stands, so the host can see what they said.
      return answer.comment ? <div className="answer-comment">↳ {answer.comment}</div> : null;
    },
  };
}

function CommentForm({
  answer,
  onSend,
  onCancel,
}: {
  answer: AnswerRow;
  onSend: (comment: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(answer.comment ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <form
      className="answer-comment-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSend(draft.trim());
      }}
    >
      <input
        ref={inputRef}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Close — which year?"
        maxLength={200}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
      />
      <button className="btn btn-sm btn-primary" type="submit">
        Send
      </button>
      {answer.comment ? (
        <button
          className="btn btn-sm btn-ghost"
          type="button"
          title="Remove the reply"
          onClick={() => onSend('')}
        >
          Clear
        </button>
      ) : null}
    </form>
  );
}

/**
 * Answers as they arrive, and the same list once the clue is settled. It cannot rule — the
 * `gradeList` tray does that in `grading` — but it shows each answer, and marks it right or
 * wrong from `verdict` once one exists, so the host keeps the results after the reveal.
 */
export function AnswerList({
  answers,
  slots,
}: {
  answers: readonly AnswerRow[];
  slots: RendererSlots;
}) {
  if (answers.length === 0) {
    return <p className="faint">Nothing submitted yet.</p>;
  }

  return (
    <ul className="answer-list">
      {answers.map((answer) => (
        <li
          key={answer.participantId}
          className={
            answer.verdict === 'correct'
              ? 'is-correct'
              : answer.verdict === 'incorrect'
                ? 'is-incorrect'
                : ''
          }
        >
          <div className="answer-main">
            <span className="buzz-name">{answer.name}</span>
            <span className="answer-text">{answer.text ?? '(submitted)'}</span>
            <span className="spacer" />
            {answer.answeredInMs !== null ? (
              <span className="answer-time" title="Time after the clue was revealed">
                {(answer.answeredInMs / 1000).toFixed(2)}s
              </span>
            ) : null}
            {answer.verdict ? (
              <span className={`tag ${answer.verdict === 'correct' ? 'tag-good' : 'tag-bad'}`}>
                {answer.verdict === 'correct' ? 'correct' : 'wrong'}
              </span>
            ) : null}
            {slots.itemAction?.(answer.participantId)}
          </div>
          {slots.itemPanel?.(answer.participantId)}
        </li>
      ))}
    </ul>
  );
}
